import InputAdornment from "@mui/material/InputAdornment";
import { MoreVertical } from "lucide-react";
import type { ReactNode } from "react";
import { Menu } from "src/components/Default/LekUI/Menu";
import { ScrollShadowWrapper } from "src/components/Default/LekUI/ScrollShadowWrapper";
import { TextFieldGrey } from "src/components/Default/LekUI/TextField";
import { Header } from "src/components/Main/ExamsBox/ExamsBox";
import isMobile from "src/helpers/isMobile";
import SearchIcon from "src/images/Icons/newIcons/search.svg";

import Box from "./Box";

interface MenuItem {
	title: string;
	onClick: () => void;
	Icon: ReactNode;
	testId?: string;
	itemTextClassName?: string;
}

interface SearchProps {
	placeholder: string;
	value: string;
	onChange: (value: string) => void;
	onClear: () => void;
	autoFocus?: boolean;
	loading?: boolean;
	className?: string;
	testId?: string;
}

interface ViewBoxProps {
	title: string;
	icon?: ReactNode;
	renderActionButton?: () => ReactNode;
	menuItems?: MenuItem[];
	tabs: string[];
	tabTestIds?: string[];
	tabsTitle?: string;
	tabsLabel?: string;
	selectedTab: number;
	onTabChange: (value: number) => void;
	searchProps?: SearchProps;
	sortButton?: ReactNode;
	withBackButton?: boolean;
	children: ReactNode;
}

export const ViewBox = ({
	title,
	icon,
	renderActionButton,
	menuItems,
	tabs,
	tabTestIds,
	tabsTitle,
	tabsLabel,
	selectedTab,
	onTabChange,
	searchProps,
	sortButton,
	withBackButton,
	children,
}: ViewBoxProps) => {
	const renderAdditionalActions = () => {
		if (!menuItems && !renderActionButton) return undefined;

		return (
			<div className="flex items-center gap-8">
				{renderActionButton?.()}
				{menuItems && (
					<Menu
						icon={<MoreVertical className="icon-small" />}
						buttonClassName="folder-menu-button"
						buttonTestId="folder-actions-menu-button"
						anchorId="folder-actions"
						closeAfterClick
						items={menuItems}
					/>
				)}
			</div>
		);
	};

	const renderSearch = searchProps
		? () => (
				<TextFieldGrey
					variant="filled"
					placeholder={searchProps.placeholder}
					value={searchProps.value}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						searchProps.onChange(e.target.value)
					}
					className={
						searchProps.className || "w-100 search--text-field max-w-350"
					}
					autoFocus={searchProps.autoFocus ?? !isMobile()}
					loading={searchProps.loading}
					onClear={searchProps.onClear}
					slotProps={{
						htmlInput: {
							"data-test-id": searchProps.testId || "view-box-search-input",
						},
						input: {
							disableUnderline: true,
							startAdornment: (
								<InputAdornment position="start" className="m-0 mr-8">
									<SearchIcon className="icon-normal icon-black-80" />
								</InputAdornment>
							),
						},
					}}
				/>
			)
		: undefined;

	return (
		<Box
			tabsClassName="folders-items-list-header"
			TabsWrapper={ScrollShadowWrapper}
			className="flex flex-col gap-16"
			headerClassName="mb-0"
			Header={
				<Header
					title={title}
					icon={icon || null}
					titleClassName="justify-between w-100"
					withBackButton={withBackButton}
					additionalActions={renderAdditionalActions()}
				/>
			}
			tabsTitle={tabsTitle}
			tabsLabel={tabsLabel}
			additionalTabsActions={renderSearch}
			tabs={tabs}
			tabTestIds={tabTestIds}
			selectedTab={selectedTab}
			onTabsChange={onTabChange}
		>
			{sortButton}
			{children}
		</Box>
	);
};
